import React from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Paper,
  Stack,
  TablePagination,
  useTheme,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import {
  getPodOrderRowKey,
  PodOrder,
} from "../../../../../models/PodOrder/PodOrder";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import {
  getModernPageColors,
  modernEmptyStateSx,
} from "../../../../../styles/modernListingPageSx";
import PodOrderListCard from "./PodOrderListCard";

export interface PodOrderListTableProps {
  loading: boolean;
  orders: PodOrder[];
  selectionEnabled?: boolean;
  isRowSelected: (order: PodOrder) => boolean;
  sortBy?: string;
  sortType?: "ASC" | "DESC";
  page: number;
  limit: number;
  totalCount: number;
  onSortChange: (column: string) => void;
  areAllSelected: () => boolean;
  areSomeSelected: () => boolean;
  onToggleSelectAll: () => void;
  onToggleRow: (order: PodOrder) => void;
  onShipmentIdClick?: (shipmentId: number) => void;
  showAvailableQty?: boolean;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const SORT_OPTIONS = [
  { key: "order_id", label: "Order ID" },
  { key: "order_date", label: "Order Date" },
];

const PodOrderListTable: React.FC<PodOrderListTableProps> = ({
  loading,
  orders,
  selectionEnabled = true,
  isRowSelected,
  sortBy,
  sortType,
  page,
  limit,
  totalCount,
  onSortChange,
  areAllSelected,
  areSomeSelected,
  onToggleSelectAll,
  onToggleRow,
  onShipmentIdClick,
  showAvailableQty = false,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "40vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper
      sx={{
        mt: 1.5,
        borderRadius: "16px",
        border: `1px solid ${colors.border}`,
        overflow: "hidden",
        boxShadow: colors.shadowSoft,
        bgcolor: colors.surface,
      }}
    >
      <Box
        sx={{
          px: { xs: 1.5, md: 2 },
          py: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          flexWrap: "wrap",
          borderBottom: `1px solid ${colors.border}`,
          bgcolor: colors.surface,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 34 }}>
          {selectionEnabled && (
            <>
              <Checkbox
                color="primary"
                size="small"
                indeterminate={areSomeSelected()}
                checked={areAllSelected()}
                disabled={orders.length === 0}
                onClick={onToggleSelectAll}
                sx={{ p: 0.5 }}
              />
              <CustomTypography variant="body2" color={colors.subtitle}>
                Select all on this page
              </CustomTypography>
            </>
          )}
        </Box>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
          <CustomTypography variant="caption" color={colors.subtitle}>
            Sort by
          </CustomTypography>
          {SORT_OPTIONS.map((option) => {
            const isActive = sortBy === option.key;
            const directionLabel = isActive && sortType === "ASC" ? "Asc" : "Desc";

            return (
              <AppButton
                key={option.key}
                label={isActive ? `${option.label}: ${directionLabel}` : option.label}
                onClick={() => onSortChange(option.key)}
                customStyles={{
                  minWidth: "fit-content",
                  height: 34,
                  px: 1.5,
                  borderRadius: "999px",
                  color: isActive ? colors.surface : colors.title,
                  backgroundColor: isActive ? theme.palette.primary.main : colors.surface,
                  border: `1px solid ${isActive ? theme.palette.primary.main : colors.border}`,
                  "&:hover": {
                    backgroundColor: isActive
                      ? theme.palette.primary.dark
                      : colors.surfaceMuted,
                  },
                }}
              />
            );
          })}
        </Stack>
      </Box>

      {orders.length === 0 ? (
        <Box
          sx={{
            ...modernEmptyStateSx(theme),
            minHeight: "24vh",
            bgcolor: colors.pageBg,
          }}
        >
          <CustomTypography variant="heading6" fontWeight={600} color={colors.subtitle}>
            No POD Orders match the current filters.
          </CustomTypography>
        </Box>
      ) : (
        <Box
          sx={{
            maxHeight: window.innerHeight - 360,
            overflowY: "auto",
            p: { xs: 1.25, md: 1.5 },
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
            bgcolor: colors.pageBg,
            "& > *": { flexShrink: 0 },
          }}
        >
          {orders.map((order) => (
            <PodOrderListCard
              key={getPodOrderRowKey(order)}
              order={order}
              selected={selectionEnabled && isRowSelected(order)}
              selectionEnabled={selectionEnabled}
              onToggleRow={onToggleRow}
              onShipmentIdClick={onShipmentIdClick}
              showAvailableQty={showAvailableQty}
            />
          ))}
        </Box>
      )}

      {totalCount > 0 && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page - 1}
          rowsPerPage={limit}
          rowsPerPageOptions={[50, 100, 200]}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          sx={{
            borderTop: `1px solid ${colors.border}`,
            backgroundColor: colors.surface,
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
              fontSize: "0.8rem",
              color: colors.subtitle,
            },
          }}
        />
      )}
    </Paper>
  );
};

export default observer(PodOrderListTable);
